import React, { useEffect, useState } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Alert,
    CircularProgress,
    Button,
    Grid,
    Card,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack,
    Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

import axios from 'axios';
import { API_PATH } from "../../../App";
import { getAxiosConfig } from "../../../helpers/AuthHelper";

interface MismatchDetails {
    emitterName: string;
    reason: string;
    workerId: string;
    algorithm: string;
}

interface IdleEmitterDetails {
    emitterName: string;
    workerList: string[];
}

interface NiceHashAuditReport {
    activeInNicehashNotInDb: MismatchDetails[];
    idleEmitters: IdleEmitterDetails[];
}

const NicehashAudit = () => {
    const [report, setReport] = useState<NiceHashAuditReport | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchReport = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API_PATH}/audit/nicehash`, getAxiosConfig());
            setReport(response.data);
        } catch (err: any) {
            console.error(err);
            setError('Failed to fetch audit report. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, []);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    const mismatchCount = report?.activeInNicehashNotInDb.length || 0;
    const idleCount = report?.idleEmitters.length || 0;

    const noIssues = mismatchCount === 0 && idleCount === 0;

    return (
        <Box p={2}>
            {/* Header with Refresh */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" fontWeight="bold">NiceHash Machine Audit</Typography>
                <Button
                    variant="contained"
                    startIcon={<ReplayIcon />}
                    onClick={fetchReport}
                    color="primary"
                >
                    Refresh
                </Button>
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={3} mb={4}>
                {noIssues && (
                    <Grid item xs={12}>
                        <Alert severity="success" icon={<CheckCircleOutlineIcon fontSize="inherit" />}>
                            All Systems Operational - No Discrepancies Found
                        </Alert>
                    </Grid>
                )}

                <Grid item xs={12} md={6}>
                    <Card sx={{ bgcolor: mismatchCount > 0 ? '#fff3e0' : 'background.paper', borderLeft: '6px solid #ff9800' }}>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography color="textSecondary" variant="overline" fontWeight="bold">Active in NiceHash / Not in DB</Typography>
                                    <Typography variant="h3" color="warning.main">{mismatchCount}</Typography>
                                </Box>
                                <WarningIcon sx={{ fontSize: 60, color: '#ff9800', opacity: 0.5 }} />
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card sx={{ bgcolor: idleCount > 0 ? '#e3f2fd' : 'background.paper', borderLeft: '6px solid #2196f3' }}>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography color="textSecondary" variant="overline" fontWeight="bold">Idle Emitters (In Redis)</Typography>
                                    <Typography variant="h3" color="info.main">{idleCount}</Typography>
                                </Box>
                                <HourglassEmptyIcon sx={{ fontSize: 60, color: '#2196f3', opacity: 0.5 }} />
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Detailed Sections (Accordions) */}

            {/* Active in NiceHash Not in DB */}
            <Accordion defaultExpanded={mismatchCount > 0} disableGutters elevation={2} sx={{ mb: 2, borderRadius: 1, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center">
                        <WarningIcon color="warning" sx={{ mr: 2 }} />
                        <Typography variant="h6" color="textPrimary">
                            Active in NiceHash / Not in DB ({mismatchCount})
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: 'grey.100' }}>
                                <TableRow>
                                    <TableCell>Emitter Name</TableCell>
                                    <TableCell>Worker ID</TableCell>
                                    <TableCell>Algorithm</TableCell>
                                    <TableCell>Reason</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {report?.activeInNicehashNotInDb.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 3, fontStyle: 'italic', color: 'text.secondary' }}>
                                            No discrepancies found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    report?.activeInNicehashNotInDb.map((row, index) => (
                                        <TableRow hover key={`${row.emitterName}-${index}`}>
                                            <TableCell sx={{ fontWeight: 500 }}>{row.emitterName}</TableCell>
                                            <TableCell>{row.workerId}</TableCell>
                                            <TableCell><Chip label={row.algorithm} size="small" variant="outlined" /></TableCell>
                                            <TableCell sx={{ color: 'text.secondary' }}>{row.reason}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </AccordionDetails>
            </Accordion>

            {/* Idle Emitters */}
            <Accordion defaultExpanded={idleCount > 0} disableGutters elevation={2} sx={{ mb: 2, borderRadius: 1, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center">
                        <HourglassEmptyIcon color="info" sx={{ mr: 2 }} />
                        <Typography variant="h6" color="textPrimary">
                            Idle Emitters ({idleCount})
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: 'grey.100' }}>
                                <TableRow>
                                    <TableCell>Emitter Name</TableCell>
                                    <TableCell>Worker List</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {report?.idleEmitters.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={2} align="center" sx={{ py: 3, fontStyle: 'italic', color: 'text.secondary' }}>
                                            No idle emitters found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    report?.idleEmitters.map((row) => (
                                        <TableRow hover key={row.emitterName}>
                                            <TableCell sx={{ fontWeight: 500 }}>{row.emitterName}</TableCell>
                                            <TableCell>
                                                {row.workerList && row.workerList.length > 0 ? (
                                                    <Box display="flex" gap={1} flexWrap="wrap">
                                                        {row.workerList.map(w => (
                                                            <Chip key={w} label={w} size="small" />
                                                        ))}
                                                    </Box>
                                                ) : (
                                                    <Typography variant="caption" color="text.disabled">No workers</Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

export default NicehashAudit;
